import { cloneDeep } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import { extractBaseTextInfoFromTextLayer, getListTextPreservePadding } from '@/utils/text-kit';
import {
    LayerType,
    LIST_CHAR_ALL,
    MaterialIdsType,
    TextAlignType,
    TextWritingType,
    type ILayerProps,
    type IListStyleMaterial,
    type ITextLayerProps,
    type ListStyleStruct,
} from '@/version-types';
import { getLayerFontFamilyIds, getListOrderFontFamily, pushHistory, updateLayer, validateInputLayer } from '../utils';

export interface IListText {
    /**
     * 应用列表文本
     *
     * @param layerID - 图层 ID
     * @param data - 列表样式配置
     * @returns
     */
    applyListText(layerID: string, data: ListStyleStruct): Promise<ILayerProps | void>;

    /**
     * 取消设置列表文本
     *
     * @param layerID - 图层 ID
     * @returns
     */
    cancelListText(layerID: string): Promise<ILayerProps | void>;

    /**
     * 更新列表素材配置
     *
     * @param layerID - 图层 ID
     * @param data - 列表素材配置
     * @param options - 选项
     * @returns
     */
    updateListStyleMaterial(
        layerID: string,
        data: IListStyleMaterial,
        options?: { writeHistory?: boolean },
    ): ILayerProps | void;
}

export class ListText implements IListText {
    private prevLayer: ILayerProps | null = null;

    constructor(private framework: IFramework) {}

    private calcLayout(prevLayer: ILayerProps, nextLayer: ILayerProps) {
        const prevLayerData = prevLayer.data as ITextLayerProps;
        const nextLayerData = nextLayer.data as ITextLayerProps;

        const prevListStyle = prevLayerData.listStyle;
        const prevListStyleMaterial = prevLayerData.listStyleMaterial;
        const nextListStyle = nextLayerData.listStyle;
        const nextListStyleMaterial = nextLayerData.listStyleMaterial;

        const { writingMode } = prevLayerData;

        let width = prevLayer.width;
        let height = prevLayer.height;

        // 添加列表文字样式（之前未设置）
        if (prevListStyle === '' && nextListStyle !== '') {
            const baseTextInfo = extractBaseTextInfoFromTextLayer(nextLayer);
            const { paddingTop, paddingLeft } = getListTextPreservePadding(baseTextInfo);

            if (writingMode === TextWritingType.HORIZONTAL_TB) {
                width = width + paddingLeft;
            } else {
                height = height + paddingTop;
            }
        }

        // 添加列表文字样式（之前已设置）
        if (
            (prevListStyle !== '' && nextListStyle !== '' && prevListStyle !== nextListStyle) ||
            (prevListStyle === 'custom' &&
                nextListStyle === 'custom' &&
                prevListStyleMaterial &&
                nextListStyleMaterial &&
                JSON.stringify(prevListStyleMaterial) !== JSON.stringify(nextListStyleMaterial))
        ) {
            const prevBaseTextInfo = extractBaseTextInfoFromTextLayer(prevLayer);
            const { paddingTop: prevPaddingTop, paddingLeft: prevPaddingLeft } =
                getListTextPreservePadding(prevBaseTextInfo);

            const nextBaseTextInfo = extractBaseTextInfoFromTextLayer(nextLayer);
            const { paddingTop: nextPaddingTop, paddingLeft: nextPaddingLeft } =
                getListTextPreservePadding(nextBaseTextInfo);

            if (writingMode === TextWritingType.HORIZONTAL_TB) {
                width = width - prevPaddingLeft + nextPaddingLeft;
            } else {
                height = height - prevPaddingTop + nextPaddingTop;
            }
        }

        // 移除列表文字样式
        if (prevListStyle !== '' && nextListStyle === '') {
            const baseTextInfo = extractBaseTextInfoFromTextLayer(prevLayer);
            const { paddingTop, paddingLeft } = getListTextPreservePadding(baseTextInfo);

            if (writingMode === TextWritingType.HORIZONTAL_TB) {
                width = width - paddingLeft;
            } else {
                height = height - paddingTop;
            }
        }

        return { width, height };
    }

    private async set(layerID: string, data: ListStyleStruct | null) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        const prevLayerData = prevLayer.data as ITextLayerProps;
        const { shapeExtension } = prevLayerData;

        if (shapeExtension) return;

        const nextLayerData: ITextLayerProps = { ...prevLayerData };

        // 设置列表样式
        if (!data) {
            // 清除列表样式
            if (prevLayerData.listStyle === '') return;

            nextLayerData.listStyle = '';
            Reflect.deleteProperty(nextLayerData, 'listStyleMaterial');
        } else {
            const style = data.style;

            // FIXME: 需要新增重复设置判断

            nextLayerData.listStyle = style;
            nextLayerData.textAlign = TextAlignType.LEFT; // 使用列表样式时，强制文本左对齐

            if (style === 'custom') {
                nextLayerData.listStyleMaterial = data.config;
            } else {
                Reflect.deleteProperty(nextLayerData, 'listStyleMaterial');
            }
        }

        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        // 设置列表序号字体
        if (data && data.style === 'custom' && data.config.fontId) {
            const fontFamily = getListOrderFontFamily(this.framework, data.config.fontId);
            if (fontFamily) {
                const runtimeData = cloneDeep(nextLayer.__runtime) || {};
                const fontFamilyMap = runtimeData.fontFamilyMap || {};
                const alreadyExist = Reflect.has(fontFamilyMap, fontFamily);

                const text = alreadyExist ? LIST_CHAR_ALL + nextLayerData.text : LIST_CHAR_ALL;
                const subset = await this.framework.createTextSubset(text, fontFamily);

                if (subset) {
                    fontFamilyMap[fontFamily] = subset;
                    runtimeData.fontFamilyMap = fontFamilyMap;
                    nextLayer.__runtime = runtimeData;
                }

                nextLayer.__listOrderFontFamily = fontFamily;
            }
        } else {
            Reflect.deleteProperty(nextLayer, '__listOrderFontFamily');
        }

        // 重新计算布局
        const nextSize = this.calcLayout(prevLayer, nextLayer);
        nextLayer.width = nextSize.width;
        nextLayer.height = nextSize.height;

        const nextMaterialIdMap = nextLayer.materialIds ? cloneDeep(nextLayer.materialIds) : {};

        nextMaterialIdMap[MaterialIdsType.TEXT_LIST] = data ? [data.id] : [];

        // 同时需要刷新字体素材 id，防止序号字体素材 id 未更新或误删除
        nextMaterialIdMap[MaterialIdsType.FONT_FAMILY] = getLayerFontFamilyIds(this.framework, nextLayer);

        nextLayer.materialIds = nextMaterialIdMap;

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].listStyle);

        return nextLayer;
    }

    async applyListText(layerID: string, data: ListStyleStruct) {
        return this.set(layerID, data);
    }

    async cancelListText(layerID: string) {
        return this.set(layerID, null);
    }

    private calcLayoutWhenScaleChanged(prevLayer: ILayerProps, nextLayer: ILayerProps) {
        const prevLayerData = prevLayer.data as ITextLayerProps;
        const nextLayerData = nextLayer.data as ITextLayerProps;

        const prevListStyleMaterial = prevLayerData.listStyleMaterial!;
        const nextListStyleMaterial = nextLayerData.listStyleMaterial!;

        const { writingMode, fontSize } = nextLayerData;
        const baseFontSize = nextListStyleMaterial.baseContentTextSize;
        const ratio = fontSize / baseFontSize;
        const prevScale = prevListStyleMaterial.scale;
        const nextScale = nextListStyleMaterial.scale;

        const prevMarkerWidth = prevListStyleMaterial.iconWidth * ratio * prevScale;
        const nextMarkerWidth = nextListStyleMaterial.iconWidth * ratio * nextScale;
        const delta = nextMarkerWidth - prevMarkerWidth;

        const ret = {
            width: nextLayer.width,
            height: nextLayer.height,
        };

        if (writingMode === TextWritingType.HORIZONTAL_TB) {
            ret.width += delta;
        } else {
            ret.height += delta;
        }

        return ret;
    }

    updateListStyleMaterial(
        layerID: string,
        data: IListStyleMaterial,
        options = { writeHistory: false },
    ): ILayerProps | void {
        const prevLayer = (() => {
            if (this.prevLayer) return this.prevLayer;
            this.prevLayer = this.framework.getLayer(layerID, true);
            return this.prevLayer;
        })();

        if (!validateInputLayer(prevLayer)) return;

        const { writeHistory = false } = options;

        const prevLayerData = prevLayer.data as ITextLayerProps;
        if (!prevLayerData.listStyleMaterial) return;

        const nextLayerData: ITextLayerProps = { ...prevLayerData, listStyleMaterial: data };
        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        // 改动缩放值，需要重新计算布局
        if (data.scale !== prevLayerData.listStyleMaterial.scale) {
            const nextSize = this.calcLayoutWhenScaleChanged(prevLayer, nextLayer);
            nextLayer.width = nextSize.width;
            nextLayer.height = nextSize.height;
        }

        updateLayer(this.framework, nextLayer);
        if (writeHistory) {
            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].listStyle);
            this.prevLayer = null;
        }

        return nextLayer;
    }
}
